import { createContext, useContext, useState, useEffect, useMemo } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1, options = {}) => {
    setCart(prev => {
      // Create a unique key based on product id and options
      const optionsKey = Object.keys(options).length > 0 
        ? JSON.stringify(options) 
        : '';
      
      const existing = prev.find(item => 
        item.product.id === product.id && 
        JSON.stringify(item.selectedOptions || {}) === JSON.stringify(options)
      );
      
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id && 
          JSON.stringify(item.selectedOptions || {}) === JSON.stringify(options)
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity, selectedOptions: options }];
    });
  };

  const removeFromCart = (productId, selectedOptions = {}) => {
    setCart(prev => prev.filter(item => 
      !(item.product.id === productId && 
        JSON.stringify(item.selectedOptions || {}) === JSON.stringify(selectedOptions))
    ));
  };

  const updateQuantity = (productId, quantity, selectedOptions = {}) => {
    if (quantity < 1) {
      removeFromCart(productId, selectedOptions);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.product.id === productId && 
        JSON.stringify(item.selectedOptions || {}) === JSON.stringify(selectedOptions)
          ? { ...item, quantity } 
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  };

  const getItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const value = useMemo(() => ({
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotal,
    getItemCount
  }), [cart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
